import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  useColorModeValue,
  Alert,
  AlertIcon,
  InputGroup,
  InputLeftElement,
  VStack,
  HStack,
  Select,
  Grid,
  GridItem,
  IconButton,
  Spinner,
} from "@chakra-ui/react";
import { PhoneIcon, RepeatIcon } from "@chakra-ui/icons";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import JalaliDatePicker from "../../components/JalaliDatePicker";
import api from "../../services/api";
import moment from "moment-jalaali";
import { getAvailableTimeSlots } from '../../services/consultantService';
import { getCaptcha } from "../../services/authService";

const DEFAULT_PAYMENT_URL = 'https://r-bank.ir/1030111030450';

function meetingTypeLabel(mt) {
  if (mt === 'phone') return 'تلفنی';
  if (mt === 'online') return 'آنلاین';
  return 'حضوری';
}

// تابع کمکی برای تبدیل consultantId به string
const getConsultantIdString = (consultantValue) => {
  if (!consultantValue) return null;
  if (typeof consultantValue === 'string') return consultantValue;
  if (typeof consultantValue === 'object') {
    return consultantValue._id || consultantValue.id || consultantValue.toString();
  }
  return String(consultantValue);
};

export default function BookAppointment() {
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isReserved, setIsReserved] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [clientData, setClientData] = useState(null);
  const [consultants, setConsultants] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedConsultant, setSelectedConsultant] = useState("");
  const [selectedTime, setSelectedTime] = useState("");
  const [bookingData, setBookingData] = useState({});
  const [sessionDuration, setSessionDuration] = useState(45);
  const [consultantRate, setConsultantRate] = useState(0);
  const [showTimeSlots, setShowTimeSlots] = useState(false);
  const [noSlotsMessage, setNoSlotsMessage] = useState("");
  const [rateText,setRateText]=useState('')
  const [meetingType, setMeetingType] = useState('in-person');
  const [paymentUrlResolved, setPaymentUrlResolved] = useState(DEFAULT_PAYMENT_URL);
  const [showBookingSessionDuration, setShowBookingSessionDuration] = useState(true);
  const [showBookingSessionCost, setShowBookingSessionCost] = useState(true);
  const [captchaSvg, setCaptchaSvg] = useState("");
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [captchaInput, setCaptchaInput] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const captchaBorder = useColorModeValue("gray.200", "gray.500");
  const captchaBoxBg = useColorModeValue("gray.50", "gray.600");

  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    trigger,
  } = useForm();

  const bgColor = useColorModeValue("white", "gray.700");

  const fetchBookingCaptcha = useCallback(async () => {
    setCaptchaLoading(true);
    try {
      const { data, token } = await getCaptcha();
      setCaptchaSvg(data);
      setCaptchaToken(token);
      setCaptchaInput("");
    } catch (e) {
      toast.error("خطا در بارگذاری کد امنیتی");
    } finally {
      setCaptchaLoading(false);
    }
  }, []);

  useEffect(() => {
    if (step === 2) {
      fetchBookingCaptcha();
    }
  }, [step, fetchBookingCaptcha]);

  // دریافت تنظیمات نمایش (مدت/هزینه جلسه) از سرور
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const res = await api.get('/settings');
        if (!isMounted) return;
        const general = res?.data?.data?.general || {};
        setShowBookingSessionDuration(general.showBookingSessionDuration !== false);
        setShowBookingSessionCost(general.showBookingSessionCost !== false);
      } catch (e) {
        // در صورت خطا، حالت پیش‌فرض (نمایش هر دو) حفظ می‌شود
      }
    })();
    return () => { isMounted = false; };
  }, []);

  // اگر توکن رزرو یک‌ساعته هنوز معتبر است، مستقیم به مرحله ۳ بپر
  // و کاربر را مجبور به ورود دوبارهٔ شماره و کد تایید نکن
  useEffect(() => {
    let isMounted = true;
    (async () => {
      const token = getValidBookingToken();
      const savedClientId = localStorage.getItem('bookingClientId');
      if (!token || !savedClientId) return;

      try {
        setIsLoading(true);
        await loadClientAndGoToStep3(token, savedClientId);
      } catch (err) {
        if (!isMounted) return;
        // در صورت خطای ۴۰۱ یا منقضی شدن توکن، پاک‌سازی و ماندن در مرحله ۱
        const status = err?.response?.status;
        const code = err?.response?.data?.code;
        if (
          status === 401 ||
          ['BOOKING_TOKEN_MISSING', 'BOOKING_TOKEN_EXPIRED', 'BOOKING_TOKEN_INVALID', 'BOOKING_CLIENT_NOT_FOUND', 'BOOKING_TOKEN_MISMATCH'].includes(code)
        ) {
          clearBookingToken();
        } else {
          console.error('Auto-restore booking session error:', err);
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    })();
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // تبدیل تاریخ میلادی به شمسی
  

  // دریافت لیست مشاوران
  const fetchConsultants = async () => {
    try {
      const response = await api.get('/users/consultants/public');
      const result = response.data;
      if (result.status === 'success') {
        const consultantsList = result.data.consultants;
        
        setConsultants(consultantsList);
        return true;
      } else {
        console.error('🔍 fetchConsultants - خطا در API:', result);
        return false;
      }
    } catch (error) {
      console.error('🔍 fetchConsultants - خطا:', error);
      if (error.response) {
        console.error('🔍 fetchConsultants - error response:', error.response.data);
        console.error('🔍 fetchConsultants - error status:', error.response.status);
      }
      return false;
    }
  };

  // ارسال کد تایید
  const sendOTP = async (phone) => {
    try {
      const response = await api.post('/auth/send-otp-appointment', { phone });
      if (response.data.status === 'success') {
        toast.success("کد تایید ارسال شد");
        return true;
      }
      const message = response.data?.message || "خطا در ارسال کد تایید";
      toast.error(message);
      setError(message);
      return false;
    } catch (error) {
      console.error('Send OTP error:', error);
      const message = error?.response?.data?.message || "خطا در ارسال کد تایید";
      toast.error(message);
      setError(message);
      return false;
    }
  };

  // تایید کد OTP
  

  // بررسی وجود مراجع
  const checkClientExists = async (phone) => {
    try {
      const response = await api.post('/clients/check-phone', { phone });
      const result = response.data;
      return result.data && result.data.exists;
    } catch (error) {
      console.error('Error checking client exists:', error);
      return false;
    }
  };

  // دریافت ساعات خالی بر اساس ساعات کاری مشاور
  const fetchAvailableSlots = async (date, consultantId) => {
    const consultantIdStr = getConsultantIdString(consultantId);

    if (!date || !consultantIdStr) {
      return;
    }

    try {
      setIsLoading(true);
      setNoSlotsMessage("");

      const availableSlotsResponse = await getAvailableTimeSlots(consultantIdStr, date);
      if (availableSlotsResponse.status !== 'success') {
        console.error('خطا در دریافت ساعات خالی');
        setAvailableSlots([]);
        return;
      }

      const slots = availableSlotsResponse.data.slots;
      const serverSessionDuration =
        typeof availableSlotsResponse?.data?.sessionDuration === 'number'
          ? availableSlotsResponse.data.sessionDuration
          : null;
      setAvailableSlots(slots);
      const reasonCode = availableSlotsResponse?.data?.reasonCode;

      if (slots.length === 0) {
        if (reasonCode === 'CONSULTANT_NOT_WORKING_DAY') {
          setNoSlotsMessage('مشاور انتخاب‌شده در این روز ساعت کاری ندارد.');
        } else if (reasonCode === 'APPOINTMENT_RANGE_ENDED') {
          setNoSlotsMessage('برای مشاور انتخاب‌شده در این تاریخ امکان نوبت‌دهی وجود ندارد.');
        } else {
          setNoSlotsMessage('ساعاتی برای این تاریخ در دسترس نیست.');
        }
      }

      // دریافت اطلاعات مشاور از لیست عمومی مشاوران
      const consultant = consultants.find(c => c._id === consultantIdStr);
      if (consultant) {
        const hourlyRate = consultant?.deposit;
        setConsultantRate(hourlyRate);
        const slotLen =
          serverSessionDuration ||
          consultant.sessionDuration ||
          consultant.showDuration ||
          60;
        setSessionDuration(slotLen);
        const priceUnitMinutes = consultant.showDuration || consultant.sessionDuration || slotLen;
        if (consultant.hourlyRate) {
          setRateText(
            `مبلغ هر ${priceUnitMinutes} دقیقه ${consultant.hourlyRate.toLocaleString('fa-IR')} تومان می باشد.`
          );
        }
      } else if (serverSessionDuration) {
        setSessionDuration(serverSessionDuration);
      }

      setShowTimeSlots(true);
    } catch (error) {
      
  // مدیریت خطاهای axios (شامل ۴۰۰ و ۵۰۰)
  if (error.message) {
    // سرور پاسخ داده اما وضعیت خطا دارد
    const serverMessage = error?.message;
    setError(serverMessage || "خطایی از سمت سرور رخ داده است");
  } else if (error.request) {
    // درخواست ارسال شده اما پاسخی دریافت نشده
    setError("خطا در ارتباط با سرور. لطفاً اینترنت خود را بررسی کنید.");
  } else {
    // خطای دیگر
    setError("خطای ناشناخته در ارسال درخواست");
  }
} finally {
      setIsLoading(false);
    }
  };

  // useEffect برای دریافت خودکار ساعات خالی هنگام تغییر تاریخ یا مشاور
  useEffect(() => {
    const date = watch('date');
    const consultantId = watch('consultantId');

    // اگر مشاور از قبل تعیین شده باشد
    const finalConsultantId = clientData?.consultant
      ? getConsultantIdString(clientData.consultant)
      : consultantId;

setError('')
    if (date && finalConsultantId) {
      fetchAvailableSlots(date, finalConsultantId);
    }
  }, [watch('date'), watch('consultantId')]);

  // useEffect جداگانه برای وقتی clientData.consultant تغییر می‌کند
  useEffect(() => {
    if (clientData?.consultant) {
      const consultantIdStr = getConsultantIdString(clientData.consultant);

      const date = watch('date');
      if (date && consultantIdStr) {
        fetchAvailableSlots(date, consultantIdStr);
      }
    }
  }, [clientData?.consultant]);

  const onSubmitStep1 = async (data) => {
    setIsLoading(true);
    setError("");
    try {
      const clientExists = await checkClientExists(data.phone);
      if (!clientExists) {
        toast.error("مراجعی با این شماره تلفن یافت نشد. لطفاً ابتدا ثبت نام کنید.");
        navigate("/auth/client-register");
        return;
      }
      const otpSent = await sendOTP(data.phone);
      // در صورت بروز خطا در ارسال کد تایید، در همین مرحله بمان و به مرحله ۲ نرو
      if (!otpSent) {
        return;
      }
      setClientData(data);
      setStep(2);
    } catch (error) {
      setError("خطا در بررسی شماره تلفن");
      toast.error("خطا در بررسی شماره تلفن");
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmitStep2 = async () => {
    if (!otpCode || otpCode.length !== 6) {
      setError("لطفاً کد ۶ رقمی را وارد کنید");
      return;
    }
    if (!captchaInput.trim()) {
      setError("لطفاً کد امنیتی را وارد کنید");
      toast.error("لطفاً کد امنیتی را وارد کنید");
      return;
    }
    setIsLoading(true);
    setError("");
    try {
      const response = await api.post('/auth/verify-otp-appointment', {
        phone: clientData.phone,
        otp: otpCode,
        captchaInput,
        captchaToken,
      });
      if (response.data.status !== 'success') {
        setError("کد تایید نامعتبر است");
        fetchBookingCaptcha();
        return;
      }
      const clientId = response.data.data.clientId;
      const bookingToken = response.data.data.token;
      // ذخیره توکن یک‌ساعته رزرو + شناسه مراجع در localStorage با کلیدهای مجزا
      // تا در صورت ورود مجدد کاربر تا یک ساعت نیاز به تایید OTP نباشد
      if (bookingToken) {
        localStorage.setItem('bookingToken', bookingToken);
        const expiresAt = Date.now() + ((response.data.data.expiresIn || 3600) * 1000);
        localStorage.setItem('bookingTokenExpiresAt', String(expiresAt));
        if (clientId) {
          localStorage.setItem('bookingClientId', String(clientId));
        }
      }
      await loadClientAndGoToStep3(bookingToken, clientId);
    } catch (error) {
      console.error('Error in onSubmitStep2:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        console.error('Error status:', error.response.status);
      }
      const message =
        error?.response?.data?.message || "خطا در تایید کد";
      setError(message);
      toast.error(message);
      fetchBookingCaptcha();
    } finally {
      setIsLoading(false);
    }
  };

  // فقط ذخیره ساعت بدون رفتن به مرحله بعد
  const onTimeSelect = (time) => {
    setSelectedTime(time);
  };

  // ✅ تابع اصلاح شده - دکمه تأیید برای رفتن به مرحله بعد
  const handleContinueToConfirmation = () => {
    if (!selectedTime) {
      setError("لطفاً ساعت را انتخاب کنید");
      return;
    }

    // تبدیل تاریخ شمسی به میلادی برای ارسال به سرور
    let dateValue = watch('date') || selectedDate;
    let gregorianDate = dateValue;

    if (dateValue && dateValue.includes('/')) {
      const gregorianMoment = moment(dateValue, 'jYYYY/jMM/jDD');
      if (gregorianMoment.isValid()) {
        gregorianMoment.hour(12).minute(0).second(0).millisecond(0);
        gregorianDate = gregorianMoment.format('YYYY-MM-DD');
      }
    }

    // ✅ اصلاح: اضافه کردن fallback به watch('consultantId')
    let consultantId = selectedConsultant ||
      getConsultantIdString(clientData?.consultant) ||
      watch('consultantId');
    const consultantIdStr = getConsultantIdString(consultantId);

    

    // ✅ اضافه شد: بررسی اینکه consultantId خالی نباشد
    if (!consultantIdStr) {
      setError("لطفاً مشاور را انتخاب کنید");
      return;
    }

    setSelectedDate(dateValue);
    setSelectedConsultant(consultantIdStr);

    const finalCost = consultantRate || 50000;

    setBookingData({
      clientId: clientData._id,
      consultantId: consultantIdStr,
      date: gregorianDate,
      time: selectedTime,
      duration: sessionDuration || 45,
      cost: finalCost,
      meetingType,
    });

    

    setStep(4);
  };

  const onDateConsultantSelect = async (data) => {

    if (!data.date) {
      setError("لطفاً تاریخ را انتخاب کنید");
      return;
    }
    if (!selectedTime) {
      setError("لطفاً ساعت را نیز انتخاب کنید");
      return;
    }
    setError("");
    setSelectedDate(data.date);

    let consultantId = data.consultantId || getConsultantIdString(clientData?.consultant);
    const consultantIdStr = getConsultantIdString(consultantId);

    setSelectedConsultant(consultantIdStr);
    setStep(4);
  };

  // تنظیم consultantId در فرم وقتی clientData تغییر می‌کند
  useEffect(() => {
    if (clientData?.consultant) {
      const consultantIdStr = getConsultantIdString(clientData.consultant);
      setValue('consultantId', consultantIdStr);
    }
  }, [clientData, setValue]);

  // پاک‌سازی توکن رزرو از localStorage
  const clearBookingToken = () => {
    localStorage.removeItem('bookingToken');
    localStorage.removeItem('bookingTokenExpiresAt');
    localStorage.removeItem('bookingClientId');
  };

  // برگرداندن توکن معتبر یا null اگر منقضی شده باشد
  const getValidBookingToken = () => {
    const token = localStorage.getItem('bookingToken');
    const expiresAt = parseInt(localStorage.getItem('bookingTokenExpiresAt') || '0', 10);
    if (!token || !expiresAt || Date.now() >= expiresAt) {
      clearBookingToken();
      return null;
    }
    return token;
  };

  // بارگذاری اطلاعات مراجع و رفتن به مرحله ۳ با استفاده از توکن رزرو معتبر
  const loadClientAndGoToStep3 = async (token, clientId) => {
    const clientResponse = await api.get(`/clients/${clientId}/public`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const result = clientResponse.data;
    if (result.status !== 'success') {
      throw new Error(result.message || 'خطا در دریافت اطلاعات مراجع');
    }

    const client = result.data.client;
    if (result.data.bookedAppointment) {
      setIsReserved(true);
      setClientData({ ...client, book: result.data.bookedAppointment });
    } else {
      setClientData(client);
    }

    try {
      await fetchConsultants();
    } catch (consultantError) {
      console.error('Error fetching consultants:', consultantError);
    }

    if (client.consultant) {
      const consultantIdStr = getConsultantIdString(client.consultant);
      setSelectedConsultant(consultantIdStr);
      setValue('consultantId', consultantIdStr);
    }

    setStep(3);
  };

  // برگشت به مرحله ۱ در صورت انقضا یا نامعتبر بودن توکن
  const handleBookingTokenExpired = (message) => {
    clearBookingToken();
    setOtpCode("");
    setSelectedTime("");
    setShowTimeSlots(false);
    setError(message || "جلسه رزرو شما منقضی شده است. لطفاً دوباره کد تایید را دریافت کنید");
    toast.error(message || "جلسه رزرو منقضی شد");
    setStep(1);
  };

  const onConfirmBooking = async () => {
    setIsLoading(true);
    setError("");
    try {
  const token = getValidBookingToken();
  if (!token) {
    handleBookingTokenExpired();
    return;
  }
  const response = await api.post('/appointments/book', bookingData, {
    headers: { Authorization: `Bearer ${token}` }
  });
  // فقط در صورت موفقیت (status 2xx) به اینجا می‌رسد
  const result = response.data;
  if (result.status === 'success') {
    clearBookingToken();
    const payUrl = result.data?.paymentUrl || DEFAULT_PAYMENT_URL;
    setPaymentUrlResolved(payUrl);
    setStep(5);
    navigator.clipboard.writeText(payUrl).catch(() => {
      console.log('کپی لینک در کلیپ‌بورد ممکن نیست');
    });
  } else {
    setError(result.message || "خطا در رزرو نوبت");
  }
} catch (error) {
  // مدیریت خطاهای axios (شامل ۴۰۰ و ۵۰۰)
  if (error.response) {
    // اگر توکن رزرو منقضی یا نامعتبر است → بازگشت به مرحله ۱
    const code = error.response.data?.code;
    if (
      error.response.status === 401 &&
      ['BOOKING_TOKEN_MISSING', 'BOOKING_TOKEN_EXPIRED', 'BOOKING_TOKEN_INVALID', 'BOOKING_CLIENT_NOT_FOUND', 'BOOKING_TOKEN_MISMATCH'].includes(code)
    ) {
      handleBookingTokenExpired(error.response.data?.message);
      return;
    }
    const serverMessage = error.response.data?.message;
    setError(serverMessage || "خطایی از سمت سرور رخ داده است");
  } else if (error.request) {
    // درخواست ارسال شده اما پاسخی دریافت نشده
    setError("خطا در ارتباط با سرور. لطفاً اینترنت خود را بررسی کنید.");
  } else {
    // خطای دیگر
    setError("خطای ناشناخته در ارسال درخواست");
  }
} finally {
      setIsLoading(false);
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case 1: return "ورود شماره تلفن";
      case 2: return "تایید شماره تلفن";
      case 3: return "انتخاب تاریخ، مشاور و ساعت";
      case 4: return "تایید رزرو";
      case 5: return "رزرو موفق";
      default: return "رزرو نوبت";
    }
  };

  const getStepDescription = () => {
    switch (step) {
      case 1: return "شماره تلفن خود را وارد کنید";
      case 2: return "کد تایید ارسال شده را وارد کنید";
      case 3: return "تاریخ، مشاور و ساعت مورد نظر را انتخاب کنید";
      case 4: return "اطلاعات رزرو را بررسی و تایید کنید";
      case 5: return "رزرو شما با موفقیت انجام شد";
      default: return "";
    }
  };

  return (
    <Flex minH="100vh" align="center" justify="center" bg={useColorModeValue("gray.50", "gray.900")}>
      <Stack spacing={8} mx="auto" maxW="lg" py={12} px={6}>
        <Stack align="center">
          <Heading fontSize="4xl" textAlign="center">
            {getStepTitle()}
          </Heading>
          <Text fontSize="lg" color="gray.600">
            {getStepDescription()}
          </Text>
        </Stack>
        <Box
          rounded="lg"
          bg={bgColor}
          boxShadow="lg"
          p={8}
        >
          {step === 1 && (
            <form onSubmit={handleSubmit(onSubmitStep1)}>
              <VStack spacing={4}>
                {error && (
                  <Alert status="error">
                    <AlertIcon />
                    {error}
                  </Alert>
                )}
                <FormControl id="phone" isInvalid={errors.phone}>
                  <FormLabel>شماره تلفن</FormLabel>
                  <InputGroup>
                    <InputLeftElement>
                      <PhoneIcon color="gray.300" />
                    </InputLeftElement>
                    <Input
                      type="tel"
                      {...register("phone", {
                        required: "شماره تلفن الزامی است",
                        pattern: {
                          value: /^(\+98|0)?9\d{9}$/,
                          message: "شماره تلفن معتبر نیست (مثال: 09123456789)",
                        },
                      })}
                      placeholder="09123456789"
                      dir="ltr"
                    />
                  </InputGroup>
                  {errors.phone && (
                    <Text color="red.500" fontSize="sm" mt={1}>
                      {errors.phone.message}
                    </Text>
                  )}
                </FormControl>
                <Button
                  type="submit"
                  colorScheme="blue"
                  size="lg"
                  isLoading={isLoading}
                  loadingText="در حال بررسی..."
                  w="full"
                >
                  ادامه
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => navigate("/client")}
                  size="md"
                >
                  بازگشت به صفحه ورود
                </Button>
              </VStack>
            </form>
          )}

          {step === 2 && (
            <VStack spacing={6}>
              {error && (
                <Alert status="error">
                  <AlertIcon />
                  {error}
                </Alert>
              )}
              <Text fontSize="md" textAlign="center">
                کد تایید ۶ رقمی به شماره {clientData?.phone} ارسال شد
              </Text>
              <FormControl>
                <FormLabel>کد تایید</FormLabel>
                <Input
                  type="text"
                  placeholder="کد 6 رقمی را وارد کنید"
                  value={otpCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setOtpCode(value);
                  }}
                  maxLength={6}
                  dir="ltr"
                  textAlign="center"
                  fontSize="lg"
                />
              </FormControl>
              <FormControl id="booking-verify-captcha">
                <FormLabel>کد امنیتی</FormLabel>
                <HStack spacing={2} align="start">
                  <Box
                    border="1px solid"
                    borderColor={captchaBorder}
                    borderRadius="md"
                    p={2}
                    bg={captchaBoxBg}
                    height="60px"
                    minW="160px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    overflow="hidden"
                    dangerouslySetInnerHTML={{ __html: captchaSvg }}
                  />
                  <IconButton
                    aria-label="تازه‌سازی کپچا"
                    icon={captchaLoading ? <Spinner size="sm" /> : <RepeatIcon />}
                    onClick={fetchBookingCaptcha}
                    isLoading={captchaLoading}
                    variant="outline"
                    colorScheme="blue"
                    size="md"
                  />
                </HStack>
                <Input
                  mt={2}
                  placeholder="متن تصویر را وارد کنید"
                  value={captchaInput}
                  onChange={(e) => setCaptchaInput(e.target.value)}
                  autoComplete="off"
                />
              </FormControl>
              <Button
                variant="link"
                color="blue.500"
                fontSize="sm"
                onClick={() => sendOTP(clientData.phone)}
                isLoading={isLoading}
                isDisabled={isLoading}
              >
                ارسال مجدد کد تایید
              </Button>
              <Stack spacing={3} w="full">
                <Button
                  colorScheme="blue"
                  size="lg"
                  isLoading={isLoading}
                  loadingText="در حال تایید..."
                  onClick={onSubmitStep2}
                  w="full"
                >
                  تایید
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setStep(1)}
                  size="md"
                  w="full"
                >
                  بازگشت
                </Button>
              </Stack>
            </VStack>
          )}

          {step === 3 && (
            isReserved ? (
              <Box
                p={6}
                bg="orange.50"
                borderRadius="lg"
                border="1px"
                borderColor="orange.200"
                textAlign="center"
              >
                <Text fontSize="lg" fontWeight="bold" color="orange.700" mb={2} align="center">
                  شما یک ملاقات در انتظار دارید
                </Text>

                <Text color="black" mb={4} align="center">
                  شما یک ملاقات در <b style={{ color: "red" }}>انتظار تایید</b> دارید و نمی‌توانید ملاقات جدید رزرو کنید
                </Text>

                <Box
                  p={4}
                  bg="white"
                  borderRadius="md"
                  border="1px"
                  borderColor="orange.300"
                  mt={4}
                >
                  <Text fontSize="sm" color="gray.600">
                    جزئیات ملاقات معلق:
                  </Text>
                  <VStack spacing={2} mt={2} align="start">
                    <Text fontSize="sm">
                      <strong>تاریخ:</strong> {new Date(clientData.book.date)?.toLocaleDateString('fa-IR', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </Text>
                    <Text fontSize="sm">
                      <strong>ساعت:</strong> {clientData.book.time}
                    </Text>
                  </VStack>
                </Box>

                <Button
                  variant="outline"
                  colorScheme="orange"
                  mt={6}
                  onClick={() => setStep(2)}
                >
                  بازگشت
                </Button>
              </Box>
            ) : (
              <form onSubmit={handleSubmit(onDateConsultantSelect)}>
                <VStack spacing={4}>
                  {error && (
                    <Alert status="error">
                      <AlertIcon />
                      {error}
                    </Alert>
                  )}
                  <JalaliDatePicker
                    value={watch("date")}
                    onChange={(value) => {
                      setValue("date", value, { shouldValidate: true });
                      setSelectedTime("");
                      setShowTimeSlots(false);
                            setNoSlotsMessage("");
                    }}
                    label="تاریخ"
                    placeholder="تاریخ را انتخاب کنید"
                    isInvalid={!!errors.date}
                    errorMessage={errors.date?.message}
                    required={true}
                    saveAsJalali={true}
                  />

                  {/* حداقل تاریخ رزرو برای مشاور */}
                  {(() => {
                    const cId = watch("consultantId") || getConsultantIdString(clientData?.consultant);
                    const c = consultants.find(x => (x._id || x.id) === cId);
                    if (c?.appointmentEndDate) {
                      const end = new Date(c.appointmentEndDate);
                      return (
                        <Text fontSize="sm" color="gray.500" mt={-2} mb={2}>
                          حداکثر تاریخ رزرو برای این مشاور: {end.toLocaleDateString('fa-IR', { year: 'numeric', month: 'long', day: 'numeric' })}
                        </Text>
                      );
                    }
                    return null;
                  })()}

                  <input
                    {...register("date", {
                      required: "تاریخ الزامی است",
                    })}
                    type="hidden"
                  />

                  {clientData?.consultant ? (
                    <FormControl>
                      <FormLabel>مشاور</FormLabel>
                      <Box p={3} bg="gray.50" borderRadius="md" border="1px" borderColor="gray.200">
                        <Text fontWeight="medium">
                          {consultants.find(c => c._id === getConsultantIdString(clientData.consultant))?.name || 'مشاور شما'}
                        </Text>
                        <Text fontSize="sm" color="gray.600" mt={1}>
                          مشاور قبلاً برای شما تعیین شده است
                        </Text>
                      </Box>
                      <input
                        {...register("consultantId", {
                          required: "مشاور الزامی است",
                          value: getConsultantIdString(clientData.consultant)
                        })}
                        type="hidden"
                      />
                    </FormControl>
                  ) : (
                    <FormControl id="consultantId" isInvalid={errors.consultantId}>
                      <FormLabel>مشاور</FormLabel>
                      <Select
                        placeholder="مشاور را انتخاب کنید"
                        {...register("consultantId", {
                          required: "مشاور الزامی است",
                        })}
                        onChange={(e) => {
                          setValue("consultantId", e.target.value);
                          setSelectedConsultant(e.target.value); // ✅ اضافه شد
                          setSelectedTime("");
                          setShowTimeSlots(false);
                          setNoSlotsMessage("");
                        }}
                      >
                        {consultants.map((consultant) => (
                          <option key={consultant._id} value={consultant._id}>
                            {consultant.name}
                          </option>
                        ))}
                      </Select>
                      {errors.consultantId && (
                        <Text color="red.500" fontSize="sm" mt={1}>
                          {errors.consultantId.message}
                        </Text>
                      )}
                    </FormControl>
                  )}

                  <FormControl>
                    <FormLabel>نوع ملاقات</FormLabel>
                    <Select
                      value={meetingType}
                      onChange={(e) => setMeetingType(e.target.value)}
                    >
                      <option value="in-person">حضوری</option>
                      <option value="phone">تلفنی</option>
                      <option value="online">آنلاین</option>
                    </Select>
                  </FormControl>

                  {/* نمایش ساعات خالی در همان مرحله */}
                  {showTimeSlots && (
                    <Box w="full" mt={4}>
                      <Text fontSize="md" fontWeight="bold" mb={3}>
                        ساعات خالی برای تاریخ {watch('date')}
                      </Text>
                      {isLoading ? (
                        <Text color="gray.500" textAlign="center">در حال بارگذاری...</Text>
                      ) : availableSlots.length > 0 ? (
                        <Grid templateColumns="repeat(3, 1fr)" gap={3} w="full">
                          {availableSlots.map((slot) => {
                            const getStatusColor = () => {
                              switch (slot.status) {
                                case 'active':
                                  return 'green';
                                case 'booked':
                                  return 'gray';
                                case 'inactive':
                                  return 'gray';
                                default:
                                  return 'gray';
                              }
                            };
                            const statusColor = getStatusColor();
                            const isSelected = selectedTime === slot.time;
                            const isClickable = slot.status === 'active';
                            return (
                              <GridItem key={slot.time}>
                                <Box
                                  cursor={isClickable ? "pointer" : "not-allowed"}
                                  onClick={() => isClickable && onTimeSelect(slot.time)}
                                  _hover={isClickable ? { bg: `${statusColor}.50` } : undefined}
                                  _active={isClickable ? { bg: `${statusColor}.100`, borderColor: `${statusColor}.500` } : undefined}
                                  border={isSelected ? "2px" : "1px"}
                                  borderColor={isSelected ? `${statusColor}.500` : "gray.200"}
                                  borderRadius="md"
                                  p={3}
                                  bg={isSelected ? `${statusColor}.50` : "white"}
                                  opacity={isClickable ? 1.5 : 0.5}
                                  transition="all 0.2s"
                                >
                                  <Text
                                    fontWeight={isSelected ? "bold" : "medium"}
                                    textAlign="center"
                                    color={isClickable ? `green` : "inherit"}
                                  >
                                    {slot.time}
                                    <span style={{ display: "block", fontSize: '10px' }}>{slot.status === 'booked' ? 'رزرو شده' : ''}</span>
                                    <span style={{ display: "block", fontSize: '10px' }}>{slot.status === 'inactive' ? '  رزرو شده' : ''}</span>
                                  </Text>
                                </Box>
                              </GridItem>
                            );
                          })}
                        </Grid>
                      ) : (
                        <Text color="gray.500" textAlign="center">
                          {noSlotsMessage || 'ساعات خالی برای این تاریخ یافت نشد'}
                        </Text>
                      )}
                    </Box>
                  )}

                  {/* نمایش ساعت انتخاب شده */}
                  {selectedTime && (
                    <Box w="full" p={3} bg="green.50" borderRadius="md" border="1px" borderColor="green.200">
                      <Text fontWeight="bold" color="green.700" textAlign="center">
                        ساعت انتخاب شده: {selectedTime}
                      </Text>
                    </Box>
                  )}

                  <Button
                    type="button"
                    colorScheme="blue"
                    size="lg"
                    w="full"
                    onClick={handleContinueToConfirmation}
                    isDisabled={!selectedTime}
                  >
                    ادامه به تایید نهایی
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setStep(2)}
                    size="md"
                    w="full"
                  >
                    بازگشت
                  </Button>
                </VStack>
              </form>
            )
          )}

          {step === 4 && (
            <VStack spacing={6}>
              {error && (
                <Alert status="error">
                  <AlertIcon />
                  {error}
                </Alert>
              )}
              <Alert status="info" borderRadius="md" mb={4}>
                <AlertIcon />
                <VStack align="start" spacing={1}>
                  <Text fontWeight="bold">پرداخت بیعانه {(consultantRate || 50000).toLocaleString('fa-IR')} تومان الزامی است در غیر اینصورت نوبت شما کنسل می گردد. </Text>
                  <Text fontSize="sm">
                    پس از تایید رزرو، برای تکمیل فرایند به لینک پرداخت مراجعه کنید
                  </Text>
                </VStack>
              </Alert>
              <Box w="full" border="1px" borderColor="gray.200" borderRadius="md" p={6} bg="white">
                <VStack spacing={3} align="stretch">
                  <HStack justify="space-between">
                    <Text fontWeight="bold">نام مراجع:</Text>
                    <Text>{clientData?.firstName} {clientData?.lastName}</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontWeight="bold">شماره تلفن:</Text>
                    <Text>{clientData?.phone}</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontWeight="bold">تاریخ:</Text>
                    <Text>{selectedDate || watch('date')}</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontWeight="bold">ساعت:</Text>
                    <Text>{selectedTime}</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontWeight="bold">مشاور:</Text>
                    <Text>{consultants.find(c => c._id === selectedConsultant)?.name}</Text>
                  </HStack>
                  <HStack justify="space-between">
                    <Text fontWeight="bold">نوع ملاقات:</Text>
                    <Text>{meetingTypeLabel(meetingType)}</Text>
                  </HStack>
                  {showBookingSessionDuration && sessionDuration > 0 && (
                    <HStack justify="space-between">
                      <Text fontWeight="bold">مدت جلسه:</Text>
                      <Text>{sessionDuration} دقیقه</Text>
                    </HStack>
                  )}
                  {(!consultantRate)  && (
                  <HStack justify="space-between">
                    <Text fontWeight="bold">بیعانه:</Text>
                    <Text color="green.600" fontWeight="bold">
                      {(consultantRate || 50000).toLocaleString('fa-IR')} تومان
                    </Text>
                  </HStack>
                  )}
                  {showBookingSessionCost && showBookingSessionDuration  && rateText && (
                    <Text fontWeight="bold">{rateText}</Text>
                  )}
                </VStack>
              </Box>
              <Stack spacing={3} w="full">
                <Button
                  colorScheme="green"
                  size="lg"
                  isLoading={isLoading}
                  loadingText="در حال رزرو..."
                  onClick={onConfirmBooking}
                  w="full"
                >
                  تایید رزرو
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setStep(3)}
                  size="md"
                  w="full"
                >
                  بازگشت
                </Button>
              </Stack>
            </VStack>
          )}

          {step === 5 && (
            <VStack spacing={6}>
              <Alert status="success" borderRadius="md">
                <AlertIcon />
                <VStack align="center" spacing={2}>
                  <Text fontWeight="bold" fontSize="lg">
                    رزرو موقت شما انجام شد!
                  </Text>
                  <Text color="red" align="center">
                    تاییدیه نهایی پس از پرداخت  بیعانه و اطلاع به کلینیک انجام خواهد شد
                  </Text>
                  <Text fontSize="md">
                    برای تایید آن به آدرس زیر بروید و مبلغ {(consultantRate || 50000).toLocaleString('fa-IR')} تومان بابت بیعانه پرداخت نمایید:
                  </Text>
                  <Box
                    p={3}
                    bg="blue.50"
                    borderRadius="md"
                    border="1px"
                    borderColor="blue.200"
                    w="full"
                  >
                    <Text
                      fontSize="md"
                      fontWeight="medium"
                      color="blue.600"
                      textAlign="center"
                      cursor="pointer"
                      onClick={() => window.open(paymentUrlResolved, '_blank')}
                      _hover={{ textDecoration: 'underline' }}
                    >
                      {paymentUrlResolved}
                    </Text>
                  </Box>
                  <Text fontSize="sm" color="gray.600">
                    لینک پرداخت در کلیپ‌بورد شما کپی شد
                  </Text>
                </VStack>
              </Alert>
              <Stack spacing={3} w="full">
                <Button
                  colorScheme="blue"
                  size="lg"
                  onClick={() => window.open(paymentUrlResolved, '_blank')}
                  w="full"
                >
                  رفتن به صفحه پرداخت
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate("/client")}
                  size="md"
                  w="full"
                >
                  بازگشت به صفحه ورود
                </Button>
              </Stack>
            </VStack>
          )}
        </Box>
      </Stack>
    </Flex>
  );
}